import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/authStore';
import { Button } from './ui/button';
import { LogOut, Settings, Sun, Moon, Plug } from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'));
  }, []);

  const toggleDark = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-40 glassmorphism border-b border-[#E2E8F0]"
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link 
            to={user ? "/dashboard" : "/"} 
            className="flex items-center space-x-2 focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2 rounded-lg"
            aria-label="Evenly Respond home page"
          >
            <div 
              className="w-8 h-8 bg-[#1d4ed8] rounded-lg flex items-center justify-center"
              aria-hidden="true"
            >
              <span className="text-white font-bold text-sm">ER</span>
            </div>
            <span className="font-bold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Evenly Respond
            </span>
          </Link>

          <nav 
            className="flex items-center space-x-4"
            role="navigation"
            aria-label="Main navigation"
          >
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                  >
                    Dashboard
                  </Button>
                </Link>
                <Link to="/qr-codes">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                  >
                    QR Codes
                  </Button>
                </Link>
                <Link to="/integrations">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                    data-testid="integrations-nav-link"
                  >
                    <Plug className="h-4 w-4 mr-1" /> Integrations
                  </Button>
                </Link>
                
                <NotificationBell />

                <button
                  onClick={toggleDark}
                  className="p-2 rounded-full hover:bg-[hsl(var(--muted))] transition-colors"
                  aria-label="Toggle dark mode"
                  data-testid="dark-mode-toggle"
                >
                  {isDark ? <Sun className="h-5 w-5 text-amber-400" /> : <Moon className="h-5 w-5 text-[#475569]" />}
                </button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/team')}>
                      Team
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/audit-log')}>
                      Audit Log
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings')}>
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/integrations')}>
                      Integrations
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      Admin Panel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/api-keys')}>
                      API Keys
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <span 
                  className="text-sm text-[#475569] hidden sm:inline"
                  aria-live="polite"
                >
                  {user.name}
                </span>
              </>
            ) : (
              <Link to="/auth">
                <Button 
                  data-testid="get-started-button" 
                  className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] btn-hover-lift focus:outline-none focus:ring-4 focus:ring-[#1d4ed8] focus:ring-offset-2"
                  aria-label="Get started with Evenly Respond"
                >
                  Get Started
                </Button>
              </Link>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};
